/**
 * Sound Utility - Provides audio feedback using Web Audio API
 * Issue #500 - Waitlist Polish Features
 */

import { getUserPreferences } from './preferences';

/**
 * Audio context singleton
 */
let audioContext: AudioContext | null = null;

/**
 * Get or create the AudioContext
 * AudioContext must be created after user interaction due to browser autoplay policies
 */
function getAudioContext(): AudioContext | null {
  if (!audioContext) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (error) {
      console.warn('Web Audio API not supported:', error);
      return null;
    }
  }
  return audioContext;
}

/**
 * Play a tone with specified frequency and duration
 * @param frequency - Frequency in Hz (e.g., 440 = A4 note)
 * @param duration - Duration in milliseconds
 * @param volume - Volume from 0 to 1 (default 0.3)
 */
function playTone(frequency: number, duration: number, volume: number = 0.3): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  try {
    // Resume audio context if suspended (required by some browsers)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = 'sine'; // Smooth sine wave

    // Fade in and out to avoid clicking
    const now = ctx.currentTime;
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(volume, now + 0.01);
    gainNode.gain.linearRampToValueAtTime(0, now + duration / 1000);

    oscillator.start(now);
    oscillator.stop(now + duration / 1000);
  } catch (error) {
    console.warn('Failed to play sound:', error);
  }
}

/**
 * Play a sequence of tones
 * @param notes - Array of {frequency, duration, delay} objects
 * @param volume - Volume from 0 to 1
 */
function playSequence(notes: Array<{ frequency: number; duration: number; delay?: number }>, volume: number = 0.3): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  try {
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    let currentTime = ctx.currentTime;
    notes.forEach(({ frequency, duration, delay = 0 }) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';

      const startTime = currentTime + delay / 1000;
      const endTime = startTime + duration / 1000;

      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.01);
      gainNode.gain.linearRampToValueAtTime(0, endTime);

      oscillator.start(startTime);
      oscillator.stop(endTime);

      currentTime = endTime;
    });
  } catch (error) {
    console.warn('Failed to play sound sequence:', error);
  }
}

/**
 * Check if sound is supported and enabled
 */
export function isSoundSupported(): boolean {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return !!(window.AudioContext || (window as any).webkitAudioContext);
}

/**
 * Play a sound effect if enabled in user preferences
 */
export function playSound(type: SoundType): boolean {
  // Check if sound is enabled in user preferences
  const preferences = getUserPreferences();
  if (!preferences.soundEnabled) {
    return false;
  }

  if (!isSoundSupported()) {
    return false;
  }

  try {
    switch (type) {
      case 'notification':
        playNotificationSound();
        break;
      case 'success':
        playSuccessSound();
        break;
      case 'error':
        playErrorSound();
        break;
      case 'click':
        playClickSound();
        break;
      case 'warning':
        playWarningSound();
        break;
      default:
        return false;
    }
    return true;
  } catch (error) {
    console.warn('Failed to play sound:', error);
    return false;
  }
}

/**
 * Sound types
 */
export type SoundType = 'notification' | 'success' | 'error' | 'click' | 'warning';

/**
 * Predefined sound effects
 */

/** Notification sound - subtle ding (C5 -> E5) */
function playNotificationSound(): void {
  playSequence([
    { frequency: 523.25, duration: 100 }, // C5
    { frequency: 659.25, duration: 150, delay: 50 }, // E5
  ], 0.2);
}

/** Success sound - pleasant ascending tones (C5 -> E5 -> G5) */
function playSuccessSound(): void {
  playSequence([
    { frequency: 523.25, duration: 80 }, // C5
    { frequency: 659.25, duration: 80, delay: 40 }, // E5
    { frequency: 783.99, duration: 150, delay: 40 }, // G5
  ], 0.25);
}

/** Error sound - descending tones (A4 -> F4) */
function playErrorSound(): void {
  playSequence([
    { frequency: 440.00, duration: 100 }, // A4
    { frequency: 349.23, duration: 200, delay: 50 }, // F4
  ], 0.2);
}

/** Click sound - very short high-pitched tone */
function playClickSound(): void {
  playTone(800, 30, 0.1);
}

/** Warning sound - two identical tones */
function playWarningSound(): void {
  playSequence([
    { frequency: 587.33, duration: 100 }, // D5
    { frequency: 587.33, duration: 100, delay: 80 }, // D5
  ], 0.2);
}

/**
 * Initialize audio context on first user interaction
 * Call this in a click/tap handler to enable audio
 */
export function initializeAudio(): void {
  const ctx = getAudioContext();
  if (ctx && ctx.state === 'suspended') {
    ctx.resume();
  }
}

/**
 * Cleanup audio context (call on unmount in SPAs)
 */
export function cleanupAudio(): void {
  if (audioContext && audioContext.state !== 'closed') {
    audioContext.close();
    audioContext = null;
  }
}
